import { Inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { AppSettings } from './../library/app-settings';
import {
  ErrorResponse,
  ListResponse,
  SimpleResponse,
} from '@core/models/response-models/response-models';
import { FileUploadRequest } from '@core/models/request-models/request-models';
import { catchError, map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { isEmpty } from 'lodash';
import {
  AlertDialogData,
  AlertDialogType,
  DialogSize,
} from '@core/utils/dialog-utils';
import { DialogFactoryService } from './dialog-factory.service';
import { AlertDialogComponent } from '@ui/components/alert-dialog/alert-dialog.component';
import { DOCUMENT } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { UFWindow } from '@models/unifocus/UFWindow.Interface';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  static TOKEN_NAME = 'SessionId';
  static TOKEN_NAME_ALT = 'SessionID';
  public languageCode = 'ENG';
  public pendingCalls: number;
  public ShowSpinner = false;

  constructor(
    public http: HttpClient,
    public dialogFactory: DialogFactoryService,
    @Inject(DOCUMENT) private readonly document: Document,
    public cookieService: CookieService
  ) {
    this.SetUnifocusSSO();
    this.pendingCalls = 0;
  }

  deleteSession() {
    this.deleteCookie(ApiService.TOKEN_NAME);
    this.deleteCookie(ApiService.TOKEN_NAME_ALT);
    this.deleteCookie('_windows');
    AppSettings.SessionID = '';
  }

  getDefaultHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'X-Requested-With': 'XMLHttpRequest',
      'Accept-Language': this.languageCode,
    });
    const sessionId = this.getSessionCookie();
    if (sessionId) {
      headers = headers.set('SessionID', sessionId)
    }
    return headers;
  }

  private SetUnifocusSSO() {
    const window: any = this.document.defaultView;
    AppSettings.EnableUnifocusSSO = !!window.unifocus;
  }

  deleteAPI(
    baseURL: string,
    URL: string,
    req = null,
    trackLoading = true
  ): Promise<any> {
    if (trackLoading) {
      this.showSpinner();
      this.pendingCalls++;
    }

    const head = this.getDefaultHeaders();

    return (
      this.http
        // .delete(baseURL + URL, { headers: head })
        .request('DELETE', baseURL + URL, { headers: head, body: req, withCredentials: true })
        .toPromise()
        .then(
          (response) => {
            if (trackLoading) {
              this.pendingCalls--;
              this.hideSpinner();
            }
            if (response != null) {
              this.checkErrorMsg(response);
            }
            return response;
          },
          (reason) => {
            if (trackLoading) {
              this.pendingCalls--;
              this.hideSpinner();
            }
            this.checkErrorMsg(reason);

            const errResponse = this.setErrorResponse(reason);
            return errResponse;
          }
        )
    );
  }

  putAPI(
    baseURL: string,
    URL: string,
    postData: any,
    trackLoading: boolean = true
  ): Promise<any> {
    if (trackLoading) {
      this.showSpinner();
      this.pendingCalls++;
    }

    const head = this.getDefaultHeaders();

    return this.http
      .put(baseURL + URL, postData, { headers: head })
      .toPromise()
      .then(
        (response) => {
          if (trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }
          if (response != null) {
            this.checkErrorMsg(response);
          }
          return response;
        },
        (reason) => {
          if (trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }
          this.checkErrorMsg(reason);
          const errResponse = this.setErrorResponse(reason);
          return errResponse;
        }
      );
  }

  postAPI(
    baseURL: string,
    URL: string,
    postData: any,
    trackLoading: boolean = true,
    responseType: string = null
  ): Promise<any> {
    if (trackLoading) {
      this.showSpinner();
      this.pendingCalls++;
    }

    const head = this.getDefaultHeaders();
    const options: any = { headers: head, withCredentials: true }
    if (responseType) {
      options.responseType = responseType;
    }
    return this.http
      .post(baseURL + URL, postData, options)
      .toPromise()
      .then(
        (response) => {
          if (trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }
          if (response != null) {
            this.checkErrorMsg(response);
          }

          return response;
        },
        (reason) => {
          if (trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }
          this.checkErrorMsg(reason);
          const errResponse = this.setErrorResponse(reason);
          return errResponse;
        }
      );
  }

  getAPI(
    baseURL: string,
    URL: string,
    trackLoading: boolean = true,
    params: any = null
  ): Promise<any> {
    if (trackLoading) {
      this.showSpinner();
      this.pendingCalls++;
    }

    const head = this.getDefaultHeaders();
    head.append('Cache-Control', 'no-cache');
    head.append('Pragma', 'no-cache');
    head.append('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT');

    const options: any = { headers: head, withCredentials: true };
    if (params) {
      options.params = params;
    }
    return this.http
      .get(baseURL + URL, options)
      .toPromise()
      .then(
        (response) => {
          if (trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }

          if (response != null) {
            this.checkErrorMsg(response);
          }

          return response;
        },
        (reason) => {
          if (trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }
          this.checkErrorMsg(reason);
          const errResponse = this.setErrorResponse(reason);
          return errResponse;
        }
      );
  }

  uploadFile<T>(
    uploadURL: string,
    formData: FormData
  ): Observable<HttpEvent<ListResponse<string>>> {
    const head = this.getDefaultHeaders();
    const req = new HttpRequest('POST', uploadURL, formData, {
      reportProgress: true,
      responseType: 'json',
      headers: head,
      withCredentials: true
    });

    return this.http.request<ListResponse<string>>(req);
  }

  downloadExcel(
    url: string,
    req,
    trackLoading = false,
    method: 'GET' | 'POST' = 'POST'
  ) {
    const headers = this.getDefaultHeaders();
    const httpRequest = new HttpRequest(method, url, req, {
      headers,
      reportProgress: true,
      responseType: 'arraybuffer',
      withCredentials: true
    });
    if (trackLoading) {
      this.showSpinner();
      this.pendingCalls++;
    }
    return this.http.request<any>(httpRequest).pipe(
      tap(
        (event) => {
          this.checkErrorMsg(event);
          if (event instanceof HttpResponse && trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }
        },
        (err) => {
          this.checkErrorMsg(err);
          if (trackLoading) {
            this.pendingCalls--;
            this.hideSpinner();
          }
        }
      )
    );
  }

  downloadFile(
    url: string,
    trackLoading = false,
    responseType: 'arraybuffer' | 'blob' | 'json' | 'text' = 'blob'
  ) {
    const headers = this.getDefaultHeaders();
    const params = new HttpParams();
    const httpRequest = new HttpRequest('GET', url, {
      headers,
      reportProgress: true,
      responseType,
      withCredentials: true,
    });
    if (trackLoading) {
      this.showSpinner();
      this.pendingCalls++;
    }
    return this.http
      .request<any>(httpRequest)
      .pipe(
        tap(
          (event) => {
            this.checkErrorMsg(event);
            if (event instanceof HttpResponse && trackLoading) {
              this.pendingCalls--;
              this.hideSpinner();
            }
          },
          (err) => {
            this.checkErrorMsg(err);
            if (trackLoading) {
              this.pendingCalls--;
              this.hideSpinner();
            }
          }
        )
      )
      .toPromise();
  }

  showSpinner(): void {
    this.ShowSpinner = true;
  }

  hideSpinner(): void {
    if (this.pendingCalls > 0) {
      return;
    }

    this.ShowSpinner = false;
  }

  setErrorResponse(reason) {
    const errorResponse = new SimpleResponse();
    errorResponse.HasError = true;
    errorResponse.Errors = [];
    errorResponse.Errors.push({
      ErrorCode: 500,
      // handle nodejs & .net errors
      ErrorMessage:
        reason?.error?.message ?? reason ?? 'ERR_SERVER_UNKNOWN_ERR',
      HasError: true,
    });
    return errorResponse;
  }

  updateErrorResponse<
    T,
    K extends keyof T,
    R extends { Errors: ErrorResponse[] }
  >(response: T, errorKey: K, result: R): R {
    if (response && response[errorKey] && !isEmpty(response[errorKey])) {
      const errResp = this.setErrorResponse(response['message']);
      result.Errors = errResp.Errors;
      result['HasError'] = errResp.HasError;
      return result;
    }
    return null;
  }

  getSessionCookie() {
    return this.getCookie(ApiService.TOKEN_NAME) || this.getCookie(ApiService.TOKEN_NAME_ALT);
  }

  getCookie(name: string): string {
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => {
          return cookie.substring(0, nameLenPlus) === `${name}=`;
        })
        .map((cookie) => {
          return decodeURIComponent(cookie.substring(nameLenPlus));
        })[0] || null
    );
  }

  deleteCookie(name: string) {
    this.cookieService.set(name, "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), '/', AppSettings.Domain, true, "Lax");
  }

  setCookie(name: string, value: string) {
    this.cookieService.set(name, value, 1, '/', AppSettings.Domain, true, "Lax");
  }

  openAlertDialog(errorMsg: string, type: string) {
    let data: any = {};
    if (type == ValidationsCheckOnURL.IPWHITELISTING) {
      data = {
        type: 'error-alert',
        title: 'ERR.ERROR',
        message: errorMsg,
        okText: 'BTN.BTN_OK',
        defaultLables: {
          title: 'Error',
          message:
            'Access from current IP address is not allowed. Please contact system administrator.',
          okText: 'Ok',
        },
      };
    } else if (type == ValidationsCheckOnURL.SESSIONSTORAGE) {
      data = {
        type: 'error-alert',
        title: 'ERR.ERROR',
        message: errorMsg,
        okText: 'BTN.BTN_OK',
        defaultLables: {
          title: 'Error',
          message: 'Invalid Login Session. Please login again',
          okText: 'Ok',
        },
      };
    }

    let dialogRef = this.dialogFactory.openSized(
      DialogSize.small,
      AlertDialogComponent,
      {
        data: data,
      }
    );
    dialogRef.afterClosed().subscribe((result) => {
      dialogRef = null;
      const ufWindow = window as unknown as UFWindow;
      if (AppSettings.EnableUnifocusSSO && ufWindow.unifocus) {
         ufWindow.unifocus.signOut(window.location.origin);
         return;
      }
      if (result) {
        window.location.assign(AppSettings.URL_Central);
      }
    });
  }

  checkErrorMsg(response) {
    if (response.error && response.error.code && response.error.code === 401) {
      this.openAlertDialog(
        response.error.message,
        ValidationsCheckOnURL.SESSIONSTORAGE
      );
    } else if (
      response.error &&
      response.error.code &&
      response.error.code === 403
    ) {
      this.openAlertDialog(
        response.error.message,
        ValidationsCheckOnURL.IPWHITELISTING
      );
    } else if (response.HasError && response.Errors != null) {
      if (response.Errors.findIndex((a) => a.ErrorCode === 100) >= 0) {
        this.openAlertDialog(
          response.Errors[0].ErrorMessage,
          ValidationsCheckOnURL.SESSIONSTORAGE
        );
      } else if (response.Errors.findIndex((a) => a.ErrorCode === 108) >= 0) {
        this.openAlertDialog(
          response.Errors[0].ErrorMessage,
          ValidationsCheckOnURL.IPWHITELISTING
        );
      }
    } else if (response && response.status && response.status === 401) {
      this.openAlertDialog(
        'ERR_INVALID_USER_TOKEN',
        ValidationsCheckOnURL.SESSIONSTORAGE
      );
    } else if (response && response.status && response.status === 403) {
      this.openAlertDialog(
        'ERR_ACCESS_FORBIDDEN_FOR_IP',
        ValidationsCheckOnURL.IPWHITELISTING
      );
    }
  }
}

export enum ValidationsCheckOnURL {
  IPWHITELISTING = 'ipWhiteListing',
  SESSIONSTORAGE = 'sessionStorage',
}
